import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import './ActivityDetails.css';

const ActivityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activityDetails, setActivityDetails] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (id) {
      fetchActivityDetails();
    }
  }, [id]);

  useEffect(() => {
    if (activityDetails && activityDetails.statusDescription) {
      setSelectedStatus(activityDetails.statusDescription);
    }
  }, [activityDetails]);

  const fetchActivityDetails = async () => {
    try {
      setLoading(true);
      const data = await apiService.get(`/activity/${id}`);
      
      if (data.activityDetails) {
        setActivityDetails(data.activityDetails);
      }
      if (data.comments && Array.isArray(data.comments)) {
        setComments(data.comments);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching activity details:', err);
      setError('Failed to load activity details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus || selectedStatus === '' || selectedStatus === activityDetails?.statusDescription) {
      return;
    }

    try {
      setUpdating(true);
      // Update the status via API
      await apiService.put('/activity/status', {
        Hactivity_monitor_id: parseInt(id),
        status_description: selectedStatus
      });
      
      alert('Status updated successfully!');
      
      // Refresh the page to get updated data
      window.location.reload();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
      // Revert to original status on error
      setSelectedStatus(activityDetails?.statusDescription || '');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      return;
    }

    try {
      setSubmittingComment(true);
      // Submit comment via API
      await apiService.post('/activity/comment', {
        Hactivity_monitor_id: parseInt(id),
        Comments: newComment.trim()
      });
      
      // Clear the textarea
      setNewComment('');
      
      // Refresh the page to show the new comment
      window.location.reload();
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Activity Details</h1>
        </div>
        <div className="loading-state">
          <p>Loading activity details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Activity Details</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => navigate('/activity-log')} className="retry-button">
            Back to Activity Log
          </button>
        </div>
      </div>
    );
  }

  if (!activityDetails) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Activity Details</h1>
        </div>
        <div className="error-state">
          <p>Activity not found</p>
          <button onClick={() => navigate('/activity-log')} className="retry-button">
            Back to Activity Log
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Activity Details</h1>
        <button onClick={() => navigate('/activity-log')} className="back-button">
          ← Back to Activity Log
        </button>
      </div>

      {/* Header Section */}
      <div className="activity-details-section">
        <div className="section-header">
          <h2>Activity Information</h2>
          <div className="status-controls">
            <button 
              onClick={handleStatusUpdate}
              disabled={updating || !selectedStatus || selectedStatus === '' || selectedStatus === activityDetails.statusDescription}
              className="update-status-btn"
            >
              {updating ? 'Updating...' : 'Update status'}
            </button>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="status-dropdown"
            >
              <option value="">Choose status</option>
              <option value="WIP">WIP</option>
              <option value="Pending">Pending</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>
        <div className="activity-details-grid">
          {/* Row 1: Status, Nature, Added by, date added */}
          <div className="detail-item">
            <label>Status:</label>
            <div className="detail-value">{activityDetails.statusDescription || 'N/A'}</div>
          </div>
          
          <div className="detail-item">
            <label>Nature:</label>
            <div className="detail-value">{activityDetails.nature || 'N/A'}</div>
          </div>
          
          <div className="detail-item">
            <label>Added by:</label>
            <div className="detail-value">{activityDetails.addedByName || 'N/A'}</div>
          </div>
          
          <div className="detail-item">
            <label>Date added:</label>
            <div className="detail-value">{formatDate(activityDetails.dateAdded)}</div>
          </div>
          
          {/* Row 2: Property, Affecting, Last update, Edited by */}
          <div className="detail-item">
            <label>Property:</label>
            <div className="detail-value">{activityDetails.propertyTitle || 'N/A'}</div>
          </div>
          
          <div className="detail-item">
            <label>Affecting:</label>
            <div className="detail-value">{activityDetails.affectingName || 'N/A'}</div>
          </div>
          
          <div className="detail-item">
            <label>Last update:</label>
            <div className="detail-value">{formatDate(activityDetails.dateEdited)}</div>
          </div>
          
          <div className="detail-item">
            <label>Edited by:</label>
            <div className="detail-value">{activityDetails.editedByName || 'N/A'}</div>
          </div>
          
          {/* Row 3: Original description (spans full width) */}
          <div className="detail-item full-width">
            <label>Original description:</label>
            <div className="detail-value">{activityDetails.comments || 'N/A'}</div>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="activity-details-section">
        <h2>Comments History</h2>
        {comments.length > 0 ? (
          <div className="comments-list">
            {comments.map((comment, index) => (
              <div key={index} className="comment-item">
                <div className="comment-header">
                  <span className="comment-author">{comment.addedByName || 'Unknown'}</span>
                  <span className="comment-date">{formatDate(comment.dateAdded)}</span>
                </div>
                <div className="comment-content">{comment.comment || 'N/A'}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-comments">
            <p>No comments available</p>
          </div>
        )}
        
        {/* Add Comment Form */}
        <div className="add-comment-section">
          <label htmlFor="newComment" className="add-comment-label">Add comments:</label>
          <form onSubmit={handleAddComment} className="add-comment-form">
            <textarea
              id="newComment"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="comment-textarea"
              placeholder="Enter your comment here..."
              rows="4"
            />
            <button
              type="submit"
              disabled={submittingComment || !newComment.trim()}
              className="submit-comment-btn"
            >
              {submittingComment ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetails;
